import React, { useCallback } from 'react';
import type { PrimitiveType } from 'intl-messageformat';
import { dismissFlag as createDismissFlag, useFlagService } from '@atlassian/jira-flags';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	type AnalyticsAttributes,
} from '@atlassian/jira-product-analytics-bridge';
import { useIntl, type MessageDescriptor } from '@atlassian/jira-intl';
import type { CrossJoinIntegraton } from '../../common/types.tsx';
import { getDynamicMessages, type EntityId } from '../../services/dynamic-messaging/index.tsx';
import type { Collaborators } from '../../services/get-collaborators/types.tsx';
import { generateId } from '../../utils/index.tsx';
import { messages } from '../../common/ui/request-access-prompt-flag/messages.tsx';

import {
	RequestAccessPromptFlag as RequestAccessPromptFlagCommon,
	type CJNMessageStrings,
} from './flag-common.tsx';

export const useRequestAccessPromptFlag = () => {
	const { showFlag, dismissFlag } = useFlagService();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	return useCallback(
		({
			users,
			onOptout,
			onSubmit,
			snoozeFor30Days,
			integration,
			eventAttributes,
			messageParser,
			featureVisitedCount = 0,
		}: {
			users: Collaborators;
			onOptout: () => unknown;
			onSubmit: () => Promise<void>;
			snoozeFor30Days: () => unknown;
			messageParser: (formatter: typeof formatMessage) => CJNMessageStrings;
			eventAttributes: AnalyticsAttributes;
			integration: CrossJoinIntegraton;
			sessionId?: string;
			featureVisitedCount?: number;
		}) => {
			const id = `request-access-${generateId()}`;

			const dismissTheFlag = () => dismissFlag(createDismissFlag(id));

			const submit = async () => {
				fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', integration, eventAttributes);
				dismissTheFlag();
				await onSubmit();
			};

			const dismiss = () => {
				fireUIAnalytics(createAnalyticsEvent({}), 'flag dismissed', integration, eventAttributes);
				dismissTheFlag();
			};

			const dismissAndOptout = () => {
				dismiss();
				fireUIAnalytics(createAnalyticsEvent({}), 'flag optedOut', integration, eventAttributes);
				onOptout();
			};

			const dismissAndSnooze = () => {
				dismissTheFlag();
				fireUIAnalytics(createAnalyticsEvent({}), 'flag snoozed', integration, eventAttributes);
				snoozeFor30Days();
			};

			fireUIAnalytics(createAnalyticsEvent({}), 'nudge viewed', integration, eventAttributes);

			const cjnMessages = messageParser(formatMessage);
			const avatarOrientation =
				integration === 'crossJoinNudgesJiraToJpd' ? 'horizontal' : 'vertical';

			showFlag({
				id,
				render: (props) => (
					<RequestAccessPromptFlagCommon
						{...props}
						id={id}
						users={users}
						onDismiss={dismiss}
						onSnoozeAndDismiss={dismissAndSnooze}
						onOptoutAndDismiss={dismissAndOptout}
						onSubmit={submit}
						avatarOrientation={avatarOrientation}
						messages={cjnMessages}
						featureVisitedCount={featureVisitedCount}
					/>
				),
			});
		},
		[formatMessage, createAnalyticsEvent, dismissFlag, showFlag],
	);
};

export const getAnalyticsAttributes = ({
	users,
	userId,
	sessionId,
	featureVisitedCount,
}: {
	users: Collaborators;
	userId: string;
	sessionId?: string;
	featureVisitedCount?: number;
}): AnalyticsAttributes => {
	return {
		collaborators: users.length,
		userId,
		sessionId,
		featureVisitedCount,
	};
};

export const getContextualBanditAnalyticsAttributes = ({
	users,
	userId,
	sessionId,
	featureVisitedCount,
	entityId,
	isContextualBandit,
}: {
	users: Collaborators;
	entityId: EntityId;
	userId: string;
	sessionId?: string;
	featureVisitedCount?: number;
	isContextualBandit: boolean;
}): AnalyticsAttributes => {
	return {
		...getAnalyticsAttributes({ users, userId, sessionId, featureVisitedCount }),
		isContextualBandit,
		entityId,
	};
};

export const getLlmAnalyticsAttributes = ({
	users,
	userId,
	sessionId,
	featureVisitedCount,
}: {
	users: Collaborators;
	userId: string;
	sessionId?: string;
	featureVisitedCount?: number;
}): AnalyticsAttributes => {
	return {
		...getAnalyticsAttributes({ users, userId, sessionId, featureVisitedCount }),
		isLLM: true,
	};
};

export const getContextualBanditCJNMessageParser = ({
	isContextualBandit,
	entityId,
	featureVisitedCount,
}: {
	isContextualBandit: boolean;
	entityId: EntityId;
	featureVisitedCount: number;
}): ((
	formatMessage: (md: MessageDescriptor, values?: Record<string, PrimitiveType>) => string,
) => CJNMessageStrings) => {
	return (
		formatMessage: (md: MessageDescriptor, values?: Record<string, PrimitiveType>) => string,
	) => {
		const dynamicMessages = getDynamicMessages(isContextualBandit, entityId) || messages;
		const infoFlagTitle = isContextualBandit
			? formatMessage(messages.title)
			: formatMessage(messages.urgencyTitle, { viewCount: featureVisitedCount });

		return {
			title: infoFlagTitle,
			description: formatMessage(dynamicMessages.description),
			joinButton: formatMessage(dynamicMessages.joinButton),
			remindMeLaterButton: formatMessage(messages.remindMeLaterButton),
			notInterestedButton: formatMessage(messages.notInterestedButton),
		};
	};
};

export const getJPDCJNMessageStrings = (
	formatMessage: (md: MessageDescriptor, values?: Record<string, PrimitiveType>) => string,
): CJNMessageStrings => {
	return {
		title: formatMessage(messages.titleJpdNudge),
		description: formatMessage(messages.descriptionJpdNudge),
		joinButton: formatMessage(messages.getStartedButton),
		remindMeLaterButton: formatMessage(messages.remindMeLaterButton),
		notInterestedButton: formatMessage(messages.doNotShowAgainButton),
	};
};

export const getLLMCJNMessageStrings = (
	formatMessage: (md: MessageDescriptor, values?: Record<string, PrimitiveType>) => string,
): CJNMessageStrings => {
	// TODO: replace
	return {
		title: formatMessage(messages.titleJpdNudge),
		description: formatMessage(messages.descriptionJpdNudge),
		joinButton: formatMessage(messages.getStartedButton),
		remindMeLaterButton: formatMessage(messages.remindMeLaterButton),
		notInterestedButton: formatMessage(messages.doNotShowAgainButton),
	};
};
